import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { cn } from "@/lib/utils";
import { ArrowRight, CheckCircle, Code, Copy, ExternalLink, Globe, Server } from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

interface DocSection {
  id: string;
  titleKey: string;
  contentKey: string;
}

const Document = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState<string>("introduction");
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(null), 2000);
    });
  };

  // 文档内容
  const sections: DocSection[] = [
    {
      id: "introduction",
      titleKey: "documentation.introduction",
      contentKey: "documentation.introduction.content",
    },
    {
      id: "api",
      titleKey: "documentation.api",
      contentKey: "documentation.api.content",
    },
  ];

  // API调用示例
  const apiExample = `curl https://api.gugudata.ai/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <GuGuData AI API Key>" \\
  -d '{
        "model": "gugudata-chat",
        "messages": [
          {"role": "system", "content": "You are a helpful assistant."},
          {"role": "user", "content": "What is the next solar term in the 24 solar terms?"}
        ],
        "stream": false
      }'`;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t("meta.documentation.title")}</title>
        <meta name="description" content={t("meta.documentation.description")} />
        <meta name="keywords" content={t("meta.documentation.keywords")} />
        <meta property="og:title" content={t("meta.documentation.title")} />
        <meta property="og:description" content={t("meta.documentation.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gugudata.com/document" />
        <link rel="canonical" href="https://gugudata.com/document" />
        <html lang={language} />
      </Helmet>

      <Navbar />
      <main className="flex-grow pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            {/* 侧边导航 */}
            <div className="w-full md:w-64 flex-shrink-0">
              <nav className="sticky top-24">
                <h2 className="text-lg font-semibold mb-4">{t("documentation.title")}</h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={cn(
                          "w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center",
                          activeSection === section.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                        )}
                      >
                        {activeSection === section.id && <ArrowRight className="mr-2 h-4 w-4" />}
                        {t(section.titleKey)}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* 主要内容区域 */}
            <div className="flex-1">
              <article className="prose prose-lg max-w-none dark:prose-invert">
                {activeSection === "introduction" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.introduction")}</h1>

                    <div className="mb-6">
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2 mb-2">MCP Protocol</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-2 mb-2">
                        AI Data Integration
                      </span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-2">API</span>
                    </div>

                    <p className="text-lg mb-6">{t("documentation.introduction.welcome")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-8">
                      <div className="flex items-start mb-4">
                        <Server className="w-6 h-6 text-brand-blue mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{t("documentation.api.baseurl.title")}</h3>
                          <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm">
                            https://api.gugudata.ai/v1
                            <button
                              onClick={() => copyToClipboard("https://api.gugudata.ai/v1", "baseurl")}
                              className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              aria-label="Copy to clipboard"
                            >
                              {copySuccess === "baseurl" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.introduction.how_it_works.title")}</h2>
                    <p className="mb-6">{t("documentation.introduction.how_it_works.description")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-8">
                      <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center">
                          <Code className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                          <span className="font-medium">{t("documentation.api.example.title")}</span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(apiExample, "apiExample")}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          aria-label="Copy to clipboard"
                        >
                          {copySuccess === "apiExample" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 p-6 rounded-b-xl overflow-x-auto text-sm">
                        <code>{apiExample}</code>
                      </pre>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.introduction.mcp_plugins.title")}</h2>
                    <p className="mb-4">{t("documentation.introduction.mcp_plugins.description")}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-blue-600 dark:text-blue-400">{t("plugins.text.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.text.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-indigo-600 dark:text-indigo-400">{t("plugins.image.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.image.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-red-600 dark:text-red-400">{t("plugins.news.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.news.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-green-600 dark:text-green-400">{t("plugins.finance.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.finance.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-amber-600 dark:text-amber-400">{t("plugins.business.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.business.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-teal-600 dark:text-teal-400">{t("plugins.location.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.location.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-orange-600 dark:text-orange-400">{t("plugins.webtools.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.webtools.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-violet-600 dark:text-violet-400">{t("plugins.ai.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.ai.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-fuchsia-600 dark:text-fuchsia-400">{t("plugins.barcode.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.barcode.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-pink-600 dark:text-pink-400">{t("plugins.sms.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.sms.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-cyan-600 dark:text-cyan-400">{t("plugins.weather.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.weather.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-emerald-600 dark:text-emerald-400">{t("plugins.education.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.education.description")}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      <a
                        href="https://www.gugudata.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center">
                          <Globe className="w-5 h-5 mr-3 text-red-500" />
                          <div>
                            <div className="font-medium">{t("documentation.introduction.mcp_plugins.chinese")}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">https://www.gugudata.com</div>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </a>

                      <a
                        href="https://www.gugudata.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center">
                          <Globe className="w-5 h-5 mr-3 text-blue-500" />
                          <div>
                            <div className="font-medium">{t("documentation.introduction.mcp_plugins.international")}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">https://www.gugudata.io</div>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </a>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 mb-8">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700 dark:text-blue-300">{t("documentation.introduction.note")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center mt-6">
                      <div className="flex items-center space-x-4">
                        <a
                          href="https://gugudata-workspace.slack.com/archives/C08KP9XEC73"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                          </svg>
                          {t("documentation.slack.support")}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "api" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.api")}</h1>

                    <div className="mb-6">
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2 mb-2">REST API</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-2 mb-2">JSON</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-2">
                        Bearer Authentication
                      </span>
                    </div>

                    <p className="mb-6">{t("documentation.api.content")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-8">
                      <div className="flex items-start mb-4">
                        <Server className="w-6 h-6 text-brand-blue mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{t("documentation.api.baseurl.title")}</h3>
                          <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm">
                            https://api.gugudata.ai/v1
                            <button
                              onClick={() => copyToClipboard("https://api.gugudata.ai/v1", "apiBaseUrl")}
                              className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              aria-label="Copy to clipboard"
                            >
                              {copySuccess === "apiBaseUrl" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.api.authentication.title")}</h2>
                    <p className="mb-6">{t("documentation.api.authentication.description")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-8">
                      <div className="px-6 py-4">
                        <h3 className="font-medium mb-2">{t("documentation.api.authentication.header")}</h3>
                        <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm mb-4">Authorization: Bearer &lt;GuGuData AI API Key&gt;</div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("documentation.api.authentication.note")}</p>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.api.endpoints.title")}</h2>

                    <div className="space-y-6 mb-8">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-3">POST</span>
                            <span className="font-mono text-sm">/chat/completions</span>
                          </div>
                        </div>
                        <div className="px-6 py-4">
                          <h3 className="font-medium mb-2">{t("documentation.api.endpoints.chat_completions.title")}</h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">{t("documentation.api.endpoints.chat_completions.description")}</p>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.chat_completions.params.title")}</h4>
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.parameter")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.type")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.required")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.description")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">model</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">string</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.yes")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.model")}</td>
                                </tr>
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">messages</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">array</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.yes")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.messages")}</td>
                                </tr>
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">stream</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">boolean</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.no")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.stream")}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.example_request")}</h4>
                          <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm mb-4">
                            <pre>
                              <code>{apiExample}</code>
                            </pre>
                          </div>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.example_response")}</h4>
                          <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                            <pre>
                              <code>{`{
  "id": "chatcmpl-123456789",
  "object": "chat.completion",
  "created": 1677858242,
  "model": "gugudata-chat",
  "choices": [
    {
      "message": {
        "role": "assistant",
        "content": "The next solar term in the 24 solar terms is Minor Heat (小暑, Xiǎoshǔ), which usually begins around July 7th when the sun reaches the celestial longitude of 105°."
      },
      "finish_reason": "stop",
      "index": 0
    }
  ],
  "usage": {
    "prompt_tokens": 25,
    "completion_tokens": 42,
    "total_tokens": 67
  }
}`}</code>
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "getting-started" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.getting-started")}</h1>
                    <p>{t("documentation.getting-started.content")}</p>
                  </div>
                )}

                {activeSection === "features" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.features")}</h1>
                    <p>{t("documentation.features.content")}</p>
                  </div>
                )}

                {activeSection === "examples" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.examples")}</h1>
                    <p>{t("documentation.examples.content")}</p>
                  </div>
                )}

                {activeSection === "troubleshooting" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.troubleshooting")}</h1>
                    <p>{t("documentation.troubleshooting.content")}</p>
                  </div>
                )}
              </article>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Document;
